import { useState } from 'react';
import { CalendarClock, CheckCircle2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useQmsPermissions } from '../../hooks/useQmsPermissions';
import type { QmsTicket } from '../../types/qms.types';
import { QmsWorkflowSignatureDialog } from './QmsWorkflowSignatureDialog';

interface QmsEffectivenessReviewPanelProps {
  ticket: QmsTicket;
  onCompleted: () => void;
}

export function QmsEffectivenessReviewPanel({
  ticket,
  onCompleted,
}: QmsEffectivenessReviewPanelProps) {
  const { canVerifyQuality } = useQmsPermissions();
  const [showSignature, setShowSignature] = useState(false);

  if (ticket.type !== 'capa' || ticket.status !== 'complete') return null;

  const dueAt = ticket.effectiveness_review_due_at
    ? new Date(ticket.effectiveness_review_due_at)
    : null;
  const isDue = dueAt ? dueAt.getTime() <= Date.now() : false;
  const verified = ticket.effectiveness_review_status === 'verified';

  return (
    <>
      <section className={`rounded-xl border p-4 ${
        verified ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
      }`}>
        <div className="flex items-start gap-3">
          {verified
            ? <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-700" />
            : <CalendarClock className="mt-0.5 h-5 w-5 text-amber-700" />}
          <div className="flex-1">
            <h4 className={`font-medium ${verified ? 'text-emerald-900' : 'text-amber-900'}`}>
              {verified ? 'CAPA effectiveness verified' : '60-day effectiveness review'}
            </h4>
            <p className={`mt-1 text-sm ${verified ? 'text-emerald-800' : 'text-amber-800'}`}>
              {verified
                ? ticket.effectiveness_review_metrics
                : dueAt
                  ? `QA verification is due ${dueAt.toLocaleString()}. Final closure remains locked until it is signed.`
                  : 'The effectiveness review will be scheduled when implementation is signed complete.'}
            </p>
            {!verified && canVerifyQuality && (
              <Button
                type="button"
                size="sm"
                className="mt-3 bg-amber-700 hover:bg-amber-800"
                onClick={() => setShowSignature(true)}
                disabled={!isDue}
              >
                <ShieldCheck className="mr-1.5 h-4 w-4" />
                {isDue ? 'Sign QA effectiveness verification' : 'Verification not due'}
              </Button>
            )}
          </div>
        </div>
      </section>

      <QmsWorkflowSignatureDialog
        open={showSignature}
        onOpenChange={setShowSignature}
        ticket={ticket}
        action="verify_effectiveness"
        onCompleted={() => onCompleted()}
      />
    </>
  );
}
